import { AuthService } from './auth';

export interface Project {
  id: string;
  name: string;
  description?: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  originalFilename: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
  files: string[];
  error?: string;
  reviewStage?: 'B' | 'C' | 'C1' | 'C2' | 'C3' | 'D';
  pageRange?: { startPage: number; endPage?: number; endnotesEnabled?: boolean; endnotesStartPage?: number };
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  file: File;
}

export class ProjectService {
  private static instance: ProjectService;
  
  private constructor() {}

  static getInstance(): ProjectService {
    if (!ProjectService.instance) {
      ProjectService.instance = new ProjectService();
    }
    return ProjectService.instance;
  }

  private getAuthHeaders(): Record<string, string> {
    const token = AuthService.getInstance().getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async createProject(projectData: CreateProjectRequest): Promise<{ success: boolean; project?: Project; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('name', projectData.name);
      if (projectData.description) {
        formData.append('description', projectData.description);
      }
      formData.append('document', projectData.file);

      const token = AuthService.getInstance().getAuthToken();
      
      const { getApiUrl } = await import('./config');
      const response = await fetch(getApiUrl('/api/projects'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, project: data.project };
      } else {
        return { success: false, error: data.error || 'Failed to create project' };
      }
    } catch (error) {
      console.error('Error creating project:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async getProjects(): Promise<{ success: boolean; projects?: Project[]; error?: string }> {
    try {
      const { getApiUrl } = await import('./config');
      const response = await fetch(getApiUrl('/api/projects'), {
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, projects: data.projects };
      } else {
        return { success: false, error: data.error || 'Failed to fetch projects' };
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async getProject(projectId: string): Promise<{ success: boolean; project?: Project; error?: string }> {
    try {
      const { getApiUrl } = await import('./config');
      const response = await fetch(getApiUrl(`/api/projects/${projectId}`), {
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, project: data.project };
      } else {
        return { success: false, error: data.error || 'Failed to fetch project' };
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async deleteProject(projectId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { getApiUrl } = await import('./config');
      const response = await fetch(getApiUrl(`/api/projects/${projectId}`), {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to delete project' };
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async updateProject(projectId: string, updates: Partial<Pick<Project, 'name' | 'description'>>): Promise<{ success: boolean; project?: Project; error?: string }> {
    try {
      const { getApiUrl } = await import('./config');
      const response = await fetch(getApiUrl(`/api/projects/${projectId}`), {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, project: data.project };
      } else {
        return { success: false, error: data.error || 'Failed to update project' };
      }
    } catch (error) {
      console.error('Error updating project:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async startWorkflow(projectId: string, options?: { startPage?: number; endPage?: number; endnotesEnabled?: boolean; endnotesStartPage?: number }): Promise<{ 
    success: boolean; 
    error?: string;
    requiresPayment?: boolean;
    paymentIntentId?: string;
    paymentIntentClientSecret?: string;
    costEstimate?: any;
    billingBreakdown?: any;
  }> {
    try {
      const token = AuthService.getInstance().getAuthToken();
      
      console.warn(`[CLIENT] 🚨 STARTING WORKFLOW for project ${projectId} with options:`, options);
      
      const { getApiUrl } = await import('./config');
      const response = await fetch(getApiUrl(`/api/projects/${projectId}/start-workflow`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options || {}),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.requiresPayment) {
          return { 
            success: true, 
            requiresPayment: true,
            paymentIntentId: data.paymentIntentId,
            paymentIntentClientSecret: data.paymentIntentClientSecret,
            costEstimate: data.costEstimate,
            billingBreakdown: data.billingBreakdown
          };
        } else {
          return { success: true };
        }
      } else {
        return { success: false, error: data.error || 'Failed to start workflow' };
      }
    } catch (error) {
      console.error('Error starting workflow:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }
} 